import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import PageLayout from '../../components/ui/PageLayout';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useToast } from '../../components/ui/Toast';

const HelpSupport = () => {
  const { user, isInitialized, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [activeTab, setActiveTab] = useState('faq');
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ticketForm, setTicketForm] = useState({ subject: '', message: '', priority: 'medium' });
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [formErrors, setFormErrors] = useState({});

  const faqs = [
    { q: 'How do I add leads to my pipeline?', a: 'Go to Lead Management and click "Add Lead" or use the Quick Actions panel. You can also import leads via CSV or use the AI Lead Finder.' },
    { q: 'How do I create an email campaign?', a: 'Navigate to Communication Hub, select the Email tab, and click "Create Campaign". You can use AI templates or write your own content.' },
    { q: 'How does AI lead scoring work?', a: 'Our AI analyzes lead data including company size, job title, engagement history, and behavioral signals to assign a score from 0-100.' },
    { q: 'Can I integrate with my CRM?', a: 'Yes! LeadGen Pro supports integrations via our API. Check the API Keys Management page for your credentials.' },
    { q: 'How do I upgrade my subscription?', a: 'Go to Subscription Management and click "Upgrade Plan" to see available plans and pricing.' },
    { q: 'How do I reset my password?', a: 'Click "Forgot Password" on the login page and enter your email. You\'ll receive a reset link within minutes.' },
    { q: 'How do I connect GoHighLevel?', a: 'Navigate to GoHighLevel Integration page, enter your API key and location ID, then click Connect. Your contacts will sync automatically.' },
    { q: 'What AI models does LeadGen Pro use?', a: 'LeadGen Pro uses OpenAI GPT-4, Anthropic Claude, and Google Gemini for different AI features across the platform.' }
  ];

  useEffect(() => {
    if (!isInitialized || authLoading) return;
    if (!user) {
      navigate('/login', { replace: true });
    }
  }, [user, isInitialized, authLoading, navigate]);

  useEffect(() => {
    if (user?.id && activeTab === 'tickets') {
      loadTickets();
    }
  }, [user?.id, activeTab]);

  const loadTickets = async () => {
    if (!user?.id || !supabase) return;
    try {
      setLoading(true);
      const { data, error } = await supabase
        ?.from('support_tickets')
        ?.select('*')
        ?.eq('user_id', user?.id)
        ?.order('created_at', { ascending: false });
      if (!error) setTickets(data || []);
    } catch (err) {
      console.error('Error loading tickets:', err);
    } finally {
      setLoading(false);
    }
  };

  const validateTicketForm = () => {
    const errors = {};
    if (!ticketForm?.subject?.trim()) errors.subject = 'Subject is required';
    if (!ticketForm?.message?.trim()) errors.message = 'Message is required';
    if (ticketForm?.message?.trim()?.length < 20) errors.message = 'Please provide more detail (at least 20 characters)';
    setFormErrors(errors);
    return Object.keys(errors)?.length === 0;
  };

  const handleSubmitTicket = async (e) => {
    e?.preventDefault();
    if (!validateTicketForm()) return;
    if (!user?.id) {
      setSubmitError('You must be logged in to submit a ticket');
      return;
    }
    setSubmitting(true);
    setSubmitError('');
    try {
      const { error } = await supabase
        ?.from('support_tickets')
        ?.insert([{
          user_id: user?.id,
          subject: ticketForm?.subject,
          message: ticketForm?.message,
          priority: ticketForm?.priority,
          status: 'open',
          created_at: new Date()?.toISOString()
        }]);
      if (error) throw error;
      const successMsg = 'Support ticket submitted successfully! We\'ll respond within 24 hours.';
      setSubmitSuccess(successMsg);
      toast?.success(successMsg, 'Ticket Submitted');
      setTicketForm({ subject: '', message: '', priority: 'medium' });
      setFormErrors({});
      setTimeout(() => setSubmitSuccess(''), 5000);
      if (activeTab === 'tickets') loadTickets();
    } catch (err) {
      const errMsg = 'Failed to submit ticket. Please try again.';
      setSubmitError(errMsg);
      toast?.error(errMsg, 'Submission Failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isInitialized || authLoading) {
    return (
      <PageLayout>
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </PageLayout>
    );
  }

  if (!user) return null;

  const tabs = [
    { id: 'faq', label: 'FAQ', icon: 'HelpCircle' },
    { id: 'contact', label: 'Contact Us', icon: 'MessageSquare' },
    { id: 'tickets', label: 'My Tickets', icon: 'Ticket' },
    { id: 'docs', label: 'Documentation', icon: 'BookOpen' }
  ];

  return (
    <PageLayout>
      <Helmet>
        <title>Help & Support - LeadGen Pro</title>
      </Helmet>

      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-foreground">Help & Support</h1>
          <p className="text-muted-foreground mt-1">Find answers, get help, and contact our support team</p>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-1 bg-muted rounded-lg p-1 mb-6">
          {tabs?.map(tab => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors flex-1 justify-center ${
                activeTab === tab?.id
                  ? 'bg-card text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name={tab?.icon} size={15} />
              {tab?.label}
            </button>
          ))}
        </div>

        {/* FAQ Tab */}
        {activeTab === 'faq' && (
          <div className="space-y-3">
            {faqs?.map((faq, index) => (
              <div key={index} className="bg-card border border-border rounded-xl p-5 shadow-elevation-1">
                <h3 className="font-semibold text-foreground mb-2 flex items-start gap-2">
                  <Icon name="HelpCircle" size={16} className="text-primary mt-0.5 flex-shrink-0" />
                  {faq?.q}
                </h3>
                <p className="text-sm text-muted-foreground pl-6">{faq?.a}</p>
              </div>
            ))}
          </div>
        )}

        {/* Contact Tab */}
        {activeTab === 'contact' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-card border border-border rounded-xl p-5 text-center shadow-elevation-1">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Icon name="Mail" size={20} className="text-blue-600" />
                </div>
                <h3 className="font-semibold text-foreground mb-1">Email Support</h3>
                <p className="text-sm text-muted-foreground mb-2">Get help via email</p>
                <a href="mailto:support@leadgenproai.us" className="text-sm text-primary hover:underline font-medium">
                  support@leadgenproai.us
                </a>
              </div>
              <div className="bg-card border border-border rounded-xl p-5 text-center shadow-elevation-1">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Icon name="Phone" size={20} className="text-green-600" />
                </div>
                <h3 className="font-semibold text-foreground mb-1">Phone Support</h3>
                <p className="text-sm text-muted-foreground mb-2">Talk to our team</p>
                <a href="tel:+18449781051" className="text-sm text-primary hover:underline font-medium">
                  (844) 978-1051
                </a>
              </div>
              <div className="bg-card border border-border rounded-xl p-5 text-center shadow-elevation-1">
                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Icon name="MessageSquare" size={20} className="text-purple-600" />
                </div>
                <h3 className="font-semibold text-foreground mb-1">Live Chat</h3>
                <p className="text-sm text-muted-foreground mb-2">Chat with support</p>
                <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">Available 9am-6pm EST</span>
              </div>
            </div>

            {/* Submit Ticket Form */}
            <div className="bg-card border border-border rounded-xl p-6 shadow-elevation-1">
              <h2 className="text-lg font-semibold text-foreground mb-4">Submit a Support Ticket</h2>
              {submitSuccess && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                  <Icon name="CheckCircle" size={16} className="text-green-600" />
                  <p className="text-sm text-green-800">{submitSuccess}</p>
                </div>
              )}
              {submitError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                  <Icon name="AlertCircle" size={16} className="text-red-600" />
                  <p className="text-sm text-red-800">{submitError}</p>
                </div>
              )}
              <form onSubmit={handleSubmitTicket} className="space-y-4" noValidate>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Subject *</label>
                  <input
                    type="text"
                    value={ticketForm?.subject}
                    onChange={e => {
                      setTicketForm(prev => ({ ...prev, subject: e?.target?.value }));
                      if (formErrors?.subject) setFormErrors(prev => ({ ...prev, subject: '' }));
                    }}
                    placeholder="Brief description of your issue"
                    className={`w-full px-3 py-2 border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors ${
                      formErrors?.subject ? 'border-red-400 bg-red-50' : 'border-border'
                    }`}
                  />
                  {formErrors?.subject && (
                    <p className="mt-1 text-xs text-red-600">{formErrors?.subject}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Priority</label>
                  <select
                    value={ticketForm?.priority}
                    onChange={e => setTicketForm(prev => ({ ...prev, priority: e?.target?.value }))}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Message *</label>
                  <textarea
                    value={ticketForm?.message}
                    onChange={e => {
                      setTicketForm(prev => ({ ...prev, message: e?.target?.value }));
                      if (formErrors?.message) setFormErrors(prev => ({ ...prev, message: '' }));
                    }}
                    placeholder="Describe your issue in detail..."
                    rows={5}
                    className={`w-full px-3 py-2 border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors resize-none ${
                      formErrors?.message ? 'border-red-400 bg-red-50' : 'border-border'
                    }`}
                  />
                  {formErrors?.message && (
                    <p className="mt-1 text-xs text-red-600">{formErrors?.message}</p>
                  )}
                </div>
                <Button type="submit" variant="primary" disabled={submitting}>
                  {submitting ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Submitting...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Icon name="Send" size={15} />
                      Submit Ticket
                    </span>
                  )}
                </Button>
              </form>
            </div>
          </div>
        )}

        {/* Tickets Tab */}
        {activeTab === 'tickets' && (
          <div className="bg-card border border-border rounded-xl p-6 shadow-elevation-1">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-foreground">My Support Tickets</h2>
              <Button variant="outline" size="sm" onClick={loadTickets} iconName="RefreshCw">
                Refresh
              </Button>
            </div>
            {loading ? (
              <div className="flex items-center justify-center py-10">
                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : tickets?.length === 0 ? (
              <div className="text-center py-10">
                <Icon name="Ticket" size={40} className="text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No support tickets yet</p>
                <p className="text-sm text-muted-foreground mt-1">Submit a ticket from the Contact Us tab</p>
              </div>
            ) : (
              <div className="space-y-3">
                {tickets?.map(ticket => (
                  <div key={ticket?.id} className="p-4 border border-border rounded-lg">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-foreground text-sm truncate">{ticket?.subject}</p>
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{ticket?.message}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          ticket?.status === 'open' ? 'bg-blue-100 text-blue-700' :
                          ticket?.status === 'resolved'? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                        }`}>
                          {ticket?.status}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          ticket?.priority === 'urgent' ? 'bg-red-100 text-red-700' :
                          ticket?.priority === 'high'? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {ticket?.priority}
                        </span>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      {new Date(ticket?.created_at)?.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Docs Tab */}
        {activeTab === 'docs' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { title: 'Getting Started Guide', desc: 'Learn the basics of LeadGen Pro', icon: 'BookOpen', color: 'blue' },
              { title: 'API Documentation', desc: 'Integrate with our REST API', icon: 'Code', color: 'purple' },
              { title: 'Video Tutorials', desc: 'Step-by-step video walkthroughs', icon: 'Play', color: 'green' },
              { title: 'Integration Guides', desc: 'Connect with third-party tools', icon: 'Link', color: 'orange' },
              { title: 'Best Practices', desc: 'Tips for maximizing results', icon: 'Star', color: 'yellow' },
              { title: 'Release Notes', desc: 'Latest features and updates', icon: 'FileText', color: 'teal' }
            ]?.map((doc, index) => (
              <div key={index} className="bg-card border border-border rounded-xl p-5 shadow-elevation-1 hover:border-primary transition-colors cursor-pointer">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 bg-${doc?.color}-100 rounded-lg flex items-center justify-center flex-shrink-0`}>
                    <Icon name={doc?.icon} size={20} className={`text-${doc?.color}-600`} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground text-sm">{doc?.title}</h3>
                    <p className="text-xs text-muted-foreground">{doc?.desc}</p>
                  </div>
                  <Icon name="ChevronRight" size={16} className="text-muted-foreground ml-auto" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default HelpSupport;